import { inject, Injectable } from '@angular/core'
import { _filterEmptyValues, _isNotNullish, _round, NumberOfPercent } from '@naturalcycles/js-lib'
import {
  Cents,
  ExperimentalPrice,
  Goal,
  HARDWARE_THERMOMETERS,
  HardwareId,
  hwIdByProductKey,
  isThermometerKey,
  Product,
  ProductDurationUnit,
  ProductKey,
  ProductType,
  ProductWithAddons,
  thermometerKeys,
  UserLocale,
  ZERO,
} from '@naturalcycles/shared'
import { TranslateService } from '@ngx-translate/core'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { PricePipe } from '@src/app/shared/pipes/price.pipe.js'
import { EnrichedAddon, EnrichedProduct } from '@src/app/shared/typings/interfaces/products.js'
import { Perk } from '../util/plansPage.util.js'
import { ouraRingDiscount } from '../util/product.util.js'
import { api } from './api.service.js'

@Injectable({ providedIn: 'root' })
export class ProductService {
  private translateService = inject(TranslateService)
  private store = inject(SignalStore)

  async getPlans(hwId: HardwareId | undefined, code?: string): Promise<Product[]> {
    const cart = this.store.$cart()
    const cartWearableVariant = cart.items.find(
      i => i.key === ProductKey.OURA_RING_DISCOUNT,
    )?.variant
    return await api.get(`product/plans`, {
      searchParams: _filterEmptyValues({ hwId, code, cartWearableVariant }),
    })
  }

  get(keys: ProductKey[] = [], types: ProductType[] = []): Product[] {
    return this.store
      .$product()
      .items.filter(product => !keys.length || keys.includes(product.key))
      .filter(product => !types.length || types.includes(product.type))
      .sort((a, b) => {
        if (types.indexOf(a.type) < types.indexOf(b.type)) return -1
        if (types.indexOf(a.type) > types.indexOf(b.type)) return 1
        if (keys.indexOf(a.key) < keys.indexOf(b.key)) return -1
        if (keys.indexOf(a.key) > keys.indexOf(b.key)) return 1
        return 0
      })
  }

  getDefaultThermometer(items: Product[]): ProductKey {
    const thermometerProduct = items.find(i => isThermometerKey(i.key))
    return thermometerProduct ? thermometerProduct.key : ProductKey.THERMOMETER
  }

  isThirdPartyDevice(hardware: ProductKey): boolean {
    return hardware !== ProductKey.B1 && !thermometerKeys.includes(hardware)
  }

  // TODO: move me to backend!
  getEnrichedAddOn(product: Product): EnrichedAddon | undefined {
    const addOn = (product as ProductWithAddons).addOns?.[0]
    if (!addOn) return

    const originalPrice = ExperimentalPrice.fromObject(addOn)
    const price = originalPrice.withDiscount(addOn.discountPercentage)
    const priceDisplay = new PricePipe().transform(price)

    // We don't use striked price for thermometers
    if (thermometerKeys.includes(addOn.key)) {
      const isFree = price.amount === 0
      return {
        ...addOn,
        selected: true,
        title: isThermometerKey(addOn.key)
          ? this.translateService.instant(`cart-${addOn.key}`)
          : addOn.title,
        uiPrice: isFree ? this.translateService.instant('tag-free') : priceDisplay,
      }
    }

    const hasDiscount = price.isLessThan(originalPrice)

    return {
      ...addOn,
      selected: true,
      uiOriginalPrice: hasDiscount ? originalPrice : undefined,
      uiPrice:
        addOn.key === ProductKey.OURA_RING_DISCOUNT
          ? this.translateService.instant('oura-ring-price-text', { price: priceDisplay })
          : priceDisplay,
    }
  }

  private getDescription(product: Product): string {
    if (product.type === ProductType.SUBSCRIPTION) {
      return ''
    }
    return isThermometerKey(product.key)
      ? this.translateService.instant('txt-device-info--default')
      : this.translateService.instant('txt-device-info--' + product.key)
  }

  private getTitle(product: Product): string {
    return product.type !== ProductType.SUBSCRIPTION
      ? this.translateService.instant('measuring-hw-name--' + product.key)
      : ''
  }

  private getEnrichedProductUi(product: Product): EnrichedProduct['ui'] {
    const translationKeySuffix = [
      ProductKey.OURA_RING_DISCOUNT,
      ProductKey.APPLE_WATCH,
      ProductKey.B1,
    ].includes(product.key)
      ? product.key
      : ProductKey.THERMOMETER

    return {
      title: this.getTitle(product),
      logo: this.isThirdPartyDevice(product.key)
        ? `/assets/img/icons/logo-black--${product.key}.svg`
        : undefined,
      subtitle: this.translateService.instant('measuring-hw-description--' + translationKeySuffix),
      description: this.getDescription(product),
    }
  }

  public enrichProducts(products: Product[], isAb395?: boolean): EnrichedProduct[] {
    return products.map(product => {
      if (isAb395) {
        if (product.key === ProductKey.MONTH) {
          product.title = 'Monthly Plan'
        }
        if (product.key === ProductKey.YEAR) {
          product.title = 'Annual Plan'
        }
        if (product.key === ProductKey.TWO_YEAR) {
          product.title = '2-Year Plan'
        }
      }
      return {
        ...product,
        addOn: this.getEnrichedAddOn(product),
        ui: this.getEnrichedProductUi(product),
      }
    })
  }

  public async getUIPlans(hwId: HardwareId): Promise<EnrichedProduct[]> {
    const { product, discountCode } = this.store.getState()
    try {
      const passDiscount = discountCode?.exists
      const plans = await this.getPlans(hwId, passDiscount ? discountCode?.code : undefined)
      return this.enrichProducts(plans)
    } catch {
      return this.enrichProducts(product.items.filter(i => i.type === ProductType.SUBSCRIPTION))
    }
  }

  /**
   * Calculates the saved percentage when subscribing yearly instead of monthly.
   * Uses `_round(n, 5)`
   */
  public getSavingPercentage(
    yearly: Product,
    plans: Product[],
    hardwarePriceAmount?: Cents,
    discountedHardwarePriceAmount?: Cents,
  ): NumberOfPercent {
    const monthly = plans.find(i => i.key === ProductKey.MONTH)
    if (!monthly || !yearly.duration) return 0

    const monthlyPrice = ExperimentalPrice.fromObject(monthly).withDiscount(
      monthly.discountPercentage,
    )
    const combinedMonthlyPrice = monthlyPrice.multiply(12).plus(hardwarePriceAmount || ZERO)
    if (combinedMonthlyPrice.amount === 0) return 0

    const yearlyPrice = ExperimentalPrice.fromObject(yearly).withDiscount(yearly.discountPercentage)
    const combinedYearlyPrice = yearlyPrice.plus(discountedHardwarePriceAmount || ZERO)

    const savings = (1 - combinedYearlyPrice.amount / combinedMonthlyPrice.amount) * 100
    return _round(savings, 5)
  }

  private getSavingsTag(
    selectedHwId: HardwareId,
    plan: EnrichedProduct | undefined,
  ): string | undefined {
    const monthlyPlan = this.store.$product().items.find(p => p.key === ProductKey.MONTH)
    let yearlyPlan = this.store.$product().items.find(p => p.key === ProductKey.YEAR)
    if (plan?.key === ProductKey.YEAR) {
      yearlyPlan = plan
    }

    let hardwarePrice: ExperimentalPrice | undefined
    if (HARDWARE_THERMOMETERS.includes(selectedHwId) || selectedHwId === HardwareId.B1) {
      const thermometerProduct = this.get(thermometerKeys).find(p => p.hwId === selectedHwId)
      if (thermometerProduct) {
        hardwarePrice = ExperimentalPrice.fromObject(thermometerProduct).withDiscount(
          thermometerProduct.discountPercentage,
        )
      }
    }

    let discountedHardwarePrice: ExperimentalPrice | undefined
    if (selectedHwId === HardwareId.B1) {
      const { addOn } = plan || {}
      discountedHardwarePrice =
        addOn && ExperimentalPrice.fromObject(addOn).withDiscount(addOn.discountPercentage)
      hardwarePrice = addOn && ExperimentalPrice.fromObject(addOn)
    }

    let savingsTag: string | undefined
    if (yearlyPlan && monthlyPlan) {
      savingsTag = `${this.getSavingPercentage(yearlyPlan, [monthlyPlan], hardwarePrice?.amount, discountedHardwarePrice?.amount)}%`
    }

    return savingsTag
  }

  private getThermometerYearlyPerks(
    selectedHwId: HardwareId,
    locale: UserLocale,
    savingsTag?: string,
    goal?: Goal,
    ab395?: boolean,
  ): Perk[] {
    return [
      {
        text: ab395
          ? `Most popular: Save ${savingsTag} yearly`
          : this.translateService.instant('txt-plan-save-amount', { savingsTag }),
      },
      {
        text: this.translateService.instant(
          locale.country === 'US' && goal === Goal.PREVENT
            ? 'perk-covered-insurance'
            : 'txt-plan-popular-choice',
        ),
      },
      {
        text: this.translateService.instant('txt-plan-free-thermo'),
        imageUrl: `https://www.datocms-assets.com/21281/${selectedHwId === HardwareId.T3_THERMOMETER ? '1745316698-t3_tiny' : '1744117904-t2_tiny'}.png`,
      },
    ]
  }

  private getAllYearlyPlanPerks(
    selectedHwId: HardwareId,
    plan: EnrichedProduct,
    params: {
      locale: UserLocale
      goal: Goal | undefined
      ownsHardware: boolean | undefined
      ab395?: boolean
    },
  ): Partial<Record<HardwareId, Perk[]>> {
    const ouraRingDiscountPercentage = ouraRingDiscount
    const savingsTag = this.getSavingsTag(selectedHwId, plan)

    const thermometerYearPlanPerks = this.getThermometerYearlyPerks(
      selectedHwId,
      params.locale,
      savingsTag,
      params.goal,
      params.ab395,
    )

    let addOnOriginalPriceDisplay = ''
    let addOnPriceDisplay = ''
    if (plan.addOn) {
      const addOnOriginalPrice = ExperimentalPrice.fromObject(plan.addOn)
      addOnOriginalPriceDisplay = new PricePipe().transform(addOnOriginalPrice)

      const addOnPrice = addOnOriginalPrice.withDiscount(plan.addOn.discountPercentage)
      addOnPriceDisplay = new PricePipe().transform(addOnPrice)
    }

    return {
      [HardwareId.ORAL_THERMOMETER]: thermometerYearPlanPerks,
      [HardwareId.UEBE_THERMOMETER]: thermometerYearPlanPerks,
      [HardwareId.T3_THERMOMETER]: thermometerYearPlanPerks,

      [HardwareId.APPLE_WATCH]: [
        {
          text: params.ab395
            ? `Most popular: Save ${savingsTag} yearly`
            : this.translateService.instant('txt-plan-save-amount', { savingsTag }),
        },
        {
          text: this.translateService.instant(
            params.locale.country === 'US' && params.goal === Goal.PREVENT
              ? 'perk-covered-insurance'
              : 'txt-plan-popular-choice',
          ),
        },
        { text: this.translateService.instant('txt-plan-less-stress') },
      ],
      [HardwareId.OURA]: [
        {
          text: params.ab395
            ? `Most popular: Save ${savingsTag} yearly`
            : this.translateService.instant('txt-plan-save-amount', { savingsTag }),
        },
        {
          text: this.translateService.instant(
            params.locale.country === 'US' && params.goal === Goal.PREVENT
              ? 'perk-covered-insurance'
              : 'txt-plan-popular-choice',
          ),
        },
        ...(params.ownsHardware
          ? [{ text: this.translateService.instant('txt-plan-less-stress') }]
          : [
              {
                text: this.translateService.instant('txt-plan-oura-discount', {
                  amount: ouraRingDiscountPercentage,
                }),
              },
            ]),
      ],
      [HardwareId.B1]: [
        {
          text: this.translateService.instant('txt-plan-save-amount', { savingsTag }),
        },
        {
          text: this.translateService.instant('perk-covered-insurance'),
        },
        {
          text: `+ <s>${addOnOriginalPriceDisplay}</s> <span><b>${addOnPriceDisplay}</b></span>  ${this.translateService.instant('product--B1--title')}`,
          imageUrl: `https://www.datocms-assets.com/21281/1746697316-tiny-b1.svg`,
        },
      ],
    }
  }

  private commonPerks(): Partial<Record<HardwareId, Perk[]>> {
    const ouraRingDiscountPercentage = ouraRingDiscount

    return {
      [HardwareId.OURA]: [
        {
          text: this.translateService.instant('txt-plan-oura-discount', {
            amount: ouraRingDiscountPercentage,
          }),
        },
      ],
    }
  }

  public getPerks(
    selectedHwId: HardwareId,
    params: {
      locale: UserLocale
      goal?: Goal
      ownsHardware?: boolean
      ab395?: boolean
    },
    plan: EnrichedProduct,
  ): Perk[] {
    const { locale, goal, ownsHardware, ab395 } = params
    const allHardwarePerksYearly = this.getAllYearlyPlanPerks(selectedHwId, plan, {
      locale,
      goal,
      ownsHardware,
      ab395,
    })
    const allHardwarePerksMonthly = this.commonPerks()
    const allHardwarePerksTwoYear = this.commonPerks()

    if (plan?.key === ProductKey.YEAR) {
      return allHardwarePerksYearly[selectedHwId] || []
    }

    if (plan?.key === ProductKey.MONTH && !ownsHardware) {
      return allHardwarePerksMonthly[selectedHwId] || []
    }

    if (plan?.key === ProductKey.TWO_YEAR) {
      return allHardwarePerksTwoYear[selectedHwId] || []
    }

    return []
  }

  private getAddonMonthlyPlan(plan: EnrichedProduct, selectedHwId: HardwareId): string | undefined {
    const thermometerHwIds = thermometerKeys.map(k => hwIdByProductKey[k]).filter(_isNotNullish)
    const hasThermometer = thermometerHwIds.includes(selectedHwId)

    let addOnPriceDisplay = ''
    if (plan.addOn) {
      const addOnPrice = ExperimentalPrice.fromObject(plan.addOn).withDiscount(
        plan.addOn.discountPercentage,
      )
      addOnPriceDisplay = new PricePipe().transform(addOnPrice)
    }

    if (plan.addOn?.optional && hasThermometer) {
      const translatedText = this.translateService.instant('txt-plan-extra-thermo')
      return `+ <span>${addOnPriceDisplay}</span> ${translatedText}`
    }

    if (plan.addOn?.key === ProductKey.B1) {
      return `+ <span>${addOnPriceDisplay}</span> ${this.translateService.instant('product--B1--title')}`
    }
  }

  // This is launching in US only, will need to add the other thermometers and b1(?) if we roll it out.
  private getTwoYearAddon(selectedHwId: HardwareId): string | undefined {
    if (selectedHwId === HardwareId.T3_THERMOMETER) {
      return '+ ' + this.translateService.instant('txt-plan-free-thermo')
    }
  }

  public getAddOnTextForPlan(selectedHwId: HardwareId, plan: EnrichedProduct): string | undefined {
    if (plan.key === ProductKey.MONTH) {
      return this.getAddonMonthlyPlan(plan, selectedHwId)
    }
    if (plan.key === ProductKey.TWO_YEAR) {
      return this.getTwoYearAddon(selectedHwId)
    }
  }

  public getTranslatedPlanTitle(plan: EnrichedProduct): string {
    return this.translateService.instant(`product-title-app--${plan.key}`)
  }

  /**
   * Returns the price per month for a given product.
   * If the product amount in units is originally a round number then then we will also round the price per month.
   * If the product price is discounted then we will not round the price per month.
   * NOTE: we're using Math.floor initially to calculate the amount per month instead of Math.round to make sure that we're always displaying the lowest price
   * which is desirable for marketing purposes
   * (This logic was translated from the original Price class in NCShared)
   */
  public pricePerMonth(product: Product, withDiscount: boolean): ExperimentalPrice | undefined {
    if (!product.duration || product.durationUnit !== ProductDurationUnit.MONTH) return undefined

    const amountPerMonth = Math.floor(product.amount / product.duration) as Cents

    // We don't want to round discounted prices
    if (withDiscount && product.discountPercentage) {
      return ExperimentalPrice.fromCents(amountPerMonth, product.currency).withDiscount(
        product.discountPercentage,
      )
    }

    const productPriceWithDiscount = ExperimentalPrice.fromObject(product).withDiscount(
      product.discountPercentage,
    )

    // If we round these prices, the "per month" price will show as the same for "test" and "test2" even though the full price is different
    const isAb395 = this.store.$product().items.some(item => item.key === ProductKey.TWO_YEAR)

    // We only want to round to the nearest unit if the original price is a round number
    const shouldRoundToNearestUnit = productPriceWithDiscount.amount % 100 === 0 && !isAb395
    const roundingPrecision = shouldRoundToNearestUnit ? 100 : 1

    const roundedAmount = _round(amountPerMonth, roundingPrecision)
    return ExperimentalPrice.fromCents(roundedAmount as Cents, product.currency)
  }
}
